import { differenceInMinutes } from "date-fns";
import { formatDateToLocale, formatIban, formatToCurrency, getInitials } from "@utils/formatting";
import { FUNDER, FUNDER_LABELS_MEDIUM, isAfdasFunder, getFunderDisplayName } from "@academy-context/shared/domain/types/enums/funders";
import { TRAINING_ITEM_TYPE, type TRAINING_ITEM_STATUS } from "@academy-context/shared/domain/types/training-items";
import {
  TRAINING_ITEM_STATUS_LABELS,
  TRAINING_ITEM_STATUS_COLORS,
  TRAINING_ITEM_TYPE_LABELS,
} from "@academy-context/shared/domain/types/training-item-labels";
import {
  ExternalProviderBatchStudentMergedStatusLabelMap,
  ExternalProviderBatchStudentMergedStatusBadgeVariants,
} from "@academy-context/shared/domain/types/enums/external-provider-batch-request";
import {
  STUDENT_HISTORY_ENTRY_TYPE,
  type StudentHistoryEntryDTO,
  type StandardLessonEntry,
  type FundingRequestEntry,
  type BalanceAdjustmentEntry,
  type RefundEntry,
  type BatchParticipationEntry,
  type ExternalProviderParticipationEntry,
} from "@shared-kernel/application/ports/student/student-repository";
import type {
  AttendanceSheetVM,
  ProviderBatchAttendanceSheetVM,
  PendingSurveyVM,
  PendingObjectiveAssignmentGroupVM,
  TrainingItemVM,
} from "@shared-kernel/application/ports/student/student-repository";
import type { TeacherLessonListVM, UnpaidLessonVM, TeacherPayoutVM } from "@shared-kernel/application/ports/teacher/teacher-repository";
import type { FUNDING_REQUEST_STATUS } from "@academy-context/read/domain/types/enums/funding-request-status";
import type { ActivityCardEntry } from "@shared-kernel/primary/shared/card-list/types";
import type { StudentFundingRequestVM } from "@shared-kernel/application/ports/student/student-repository";
import { TEACHER_PAYOUT_STATUS } from "@invoicing-context/read/domain/types/teacher/payout";
import type { PayoutLessonVM } from "@shared-kernel/application/ports/shared/shared-repository";
import { teacherGetLocationName } from "@academy-context/write/domain/services/locations";

const formatDateRange = (start: string, end: string): string => {
  return `${formatDateToLocale(start)} → ${formatDateToLocale(end)}`;
};

const formatDuration = (start: string, end: string): string => {
  const totalMinutes = differenceInMinutes(new Date(end), new Date(start));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0 ? `${hours}h${minutes.toString().padStart(2, "0")}` : `${hours}h`;
};

const formatHours = (decimalHours: number): string => {
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  return minutes > 0 ? `${hours}h${minutes.toString().padStart(2, "0")}` : `${hours}h`;
};

const formatLessonDateAndHours = (date: string, hours: number): string => {
  return `${formatDateToLocale(date)} • ${formatHours(hours)}`;
};

const getFunderInitials = (funderType: FUNDER | "afdas_hors_is"): string => {
  if (funderType === "afdas_hors_is" || isAfdasFunder(funderType)) return "AF";
  if (funderType === FUNDER.FRANCE_TRAVAIL) return "FT";
  const label = FUNDER_LABELS_MEDIUM[funderType];
  return label.substring(0, 2).toUpperCase();
};

function formatStandardLesson(entry: StandardLessonEntry): ActivityCardEntry {
  const fullName = `${entry.teacherName} ${entry.teacherLastName}`;
  return {
    avatarInitials: getInitials(fullName),
    primaryText: fullName,
    secondaryText: formatLessonDateAndHours(entry.lessonDate, entry.hours),
    tertiaryText: entry.studioPrice > 0 ? `${entry.studioLabel} • ${formatToCurrency(entry.studioPrice)}/h` : entry.studioLabel,
    badge: "Coaching individuel",
    badgeVariant: "default",
  };
}

function formatFundingRequest(entry: FundingRequestEntry): ActivityCardEntry {
  let funderLabel;
  if (entry.funderType === "afdas_hors_is") {
    funderLabel = "AFDAS - Hors Intermittent du spectacle";
  } else {
    funderLabel = FUNDER_LABELS_MEDIUM[entry.funderType];
  }
  return {
    avatarInitials: getFunderInitials(entry.funderType),
    primaryText: funderLabel,
    secondaryText: formatDateRange(entry.trainingInterval.start, entry.trainingInterval.end),
    tertiaryText: entry.trainingTitle,
    badge: "Financement",
    badgeVariant: "default",
  };
}

function formatBalanceAdjustment(entry: BalanceAdjustmentEntry): ActivityCardEntry {
  return {
    avatarInitials: getInitials(entry.label),
    primaryText: entry.label,
    secondaryText: entry.note ?? "",
    tertiaryText: null,
    badge: "Ajustement de solde",
    badgeVariant: "default",
  };
}

function formatRefund(entry: RefundEntry): ActivityCardEntry {
  const label = entry.paymentLabel ?? "Remboursement";
  return {
    avatarInitials: getInitials(label),
    primaryText: label,
    secondaryText: formatIban(entry.iban),
    tertiaryText: null,
    badge: "Remboursement",
    badgeVariant: "default",
  };
}

function formatParticipation(entry: BatchParticipationEntry): ActivityCardEntry {
  return {
    avatarInitials: getInitials(entry.organizer.userName),
    primaryText: entry.organizer.userName,
    secondaryText: formatDateRange(entry.dateInterval.start, entry.dateInterval.end),
    tertiaryText: entry.batchTitle,
    badge: "Masterclass",
    badgeVariant: "default",
    secondaryBadge: entry.invoiceStatus === "unpaid" ? "Provisionné - non réglé" : null,
    secondaryBadgeVariant: entry.invoiceStatus === "unpaid" ? "warning" : undefined,
  };
}

function formatExternalProviderParticipation(entry: ExternalProviderParticipationEntry): ActivityCardEntry {
  const isPending = entry.paymentStatus === "pending" || entry.paymentStatus === null;
  return {
    avatarInitials: getInitials(entry.providerName),
    primaryText: entry.providerName,
    secondaryText: formatDateRange(entry.dateInterval.start, entry.dateInterval.end),
    tertiaryText: entry.trainingTitle,
    badge: "Masterclass externe",
    badgeVariant: "default",
    secondaryBadge: isPending ? "Provisionné - non réglé" : null,
    secondaryBadgeVariant: isPending ? "warning" : undefined,
  };
}

export function formatEntry(entry: StudentHistoryEntryDTO): ActivityCardEntry {
  switch (entry.type) {
    case STUDENT_HISTORY_ENTRY_TYPE.STANDARD_LESSON:
      return formatStandardLesson(entry);
    case STUDENT_HISTORY_ENTRY_TYPE.FUNDING_REQUEST:
      return formatFundingRequest(entry);
    case STUDENT_HISTORY_ENTRY_TYPE.BALANCE_ADJUSTMENT:
      return formatBalanceAdjustment(entry);
    case STUDENT_HISTORY_ENTRY_TYPE.REFUND:
      return formatRefund(entry);
    case STUDENT_HISTORY_ENTRY_TYPE.PARTICIPATION:
      return formatParticipation(entry);
    case STUDENT_HISTORY_ENTRY_TYPE.EXTERNAL_PROVIDER_PARTICIPATION:
      return formatExternalProviderParticipation(entry);
    default: {
      const unknownEntry = entry as unknown as { type: string };
      throw new Error(`Unknown student history entry type: ${unknownEntry.type}`);
    }
  }
}

export function formatAttendanceSheet(sheet: AttendanceSheetVM): ActivityCardEntry {
  const advisorName = sheet.details.educationalAdvisor.userName;

  if (sheet.type === TRAINING_ITEM_TYPE.ONE_ON_ONE_LESSONS) {
    const lessonSheet = sheet;
    return {
      avatarInitials: getInitials(advisorName),
      primaryText: advisorName,
      secondaryText: formatLessonDateAndHours(lessonSheet.date, lessonSheet.hours),
      tertiaryText:
        lessonSheet.details.lesson.studioPrice > 0
          ? `${lessonSheet.details.lesson.studio} • ${formatToCurrency(lessonSheet.details.lesson.studioPrice)}/h`
          : lessonSheet.details.lesson.studio,
      badge: "Coaching individuel",
      badgeVariant: "default",
    };
  }

  const batchSheet = sheet as ProviderBatchAttendanceSheetVM;
  const isExternal = sheet.type === TRAINING_ITEM_TYPE.EXTERNAL_PROVIDER_BATCH;
  return {
    avatarInitials: getInitials(advisorName),
    primaryText: advisorName,
    secondaryText: `${formatDateToLocale(batchSheet.interval.start)} • ${formatDuration(
      batchSheet.interval.start,
      batchSheet.interval.end,
    )}`,
    tertiaryText: batchSheet.details.label,
    badge: isExternal ? "Masterclass externe" : "Masterclass",
    badgeVariant: "default",
  };
}

const FUNDING_REQUEST_STATUS_LABELS: Record<FUNDING_REQUEST_STATUS, string> = {
  to_review: "Demande reçue",
  reviewed: "Demande reçue",
  submitted_to_funder: "Attente réponse AFDAS",
  approved: "Validé",
  rejected: "Annulé",
};

const FUNDING_REQUEST_STATUS_VARIANTS: Record<FUNDING_REQUEST_STATUS, ActivityCardEntry["badgeVariant"]> = {
  to_review: "default",
  reviewed: "default",
  submitted_to_funder: "default",
  approved: "success",
  rejected: "warning",
};

export function formatFundingRequestItem(funding: StudentFundingRequestVM): ActivityCardEntry {
  let funderLabel;
  if (funding.funder.type === "afdas_hors_is") {
    funderLabel = "AFDAS - Hors Intermittent du spectacle";
  } else {
    funderLabel = getFunderDisplayName(funding.funder, false);
  }
  const intervalText = funding.interval
    ? `${formatDateToLocale(funding.interval.start)} → ${formatDateToLocale(funding.interval.end)}`
    : null;

  return {
    avatarInitials: funderLabel.slice(0, 2).toUpperCase(),
    primaryText: funderLabel,
    secondaryText: intervalText ?? "",
    tertiaryText: funding.trainingName,
    badge: FUNDING_REQUEST_STATUS_LABELS[funding.status],
    badgeVariant: FUNDING_REQUEST_STATUS_VARIANTS[funding.status],
  };
}

export function formatPendingSurvey(survey: PendingSurveyVM): ActivityCardEntry {
  return {
    avatarInitials: "QS",
    primaryText: "Questionnaire de satisfaction",
    secondaryText: survey.trainingContext.title,
    tertiaryText: formatDateRange(survey.trainingContext.interval.start, survey.trainingContext.interval.end),
    badge: "En attente",
    badgeVariant: "warning",
  };
}

// Ce que l'élève a à faire, c'est assigner un intervenant à ses OBJECTIFS. L'évaluation, elle, est
// l'acte du conseiller — l'élève n'en rend aucune.
export function formatPendingObjectiveGroup(group: PendingObjectiveAssignmentGroupVM): ActivityCardEntry {
  const objectiveCount = group.objectives.length;
  const objectiveLabel = objectiveCount === 1 ? "objectif" : "objectifs";

  return {
    avatarInitials: "OB",
    primaryText: `${objectiveCount} ${objectiveLabel} à assigner`,
    secondaryText: group.trainingContext.title,
    tertiaryText: formatDateRange(group.trainingContext.interval.start, group.trainingContext.interval.end),
    badge: "En attente",
    badgeVariant: "warning",
  };
}

const mapStatusToBadgeVariant = (status: TRAINING_ITEM_STATUS): ActivityCardEntry["badgeVariant"] => {
  const colorMap: Record<string, ActivityCardEntry["badgeVariant"]> = {
    secondary: "default",
    default: "default",
    success: "success",
    outline: "default",
    destructive: "destructive",
  };
  return colorMap[TRAINING_ITEM_STATUS_COLORS[status]] ?? "default";
};

export function formatTrainingItem(item: TrainingItemVM): ActivityCardEntry {
  // Handle ExternalProviderBatchTrainingItemVM separately due to different status type
  if (item.itemType === TRAINING_ITEM_TYPE.EXTERNAL_PROVIDER_BATCH) {
    return {
      avatarInitials: getInitials(item.details.label),
      primaryText: item.title,
      secondaryText: formatDateRange(item.interval.start, item.interval.end),
      tertiaryText: item.details.label,
      badge: item.isActionRequired ? "Action requise" : ExternalProviderBatchStudentMergedStatusLabelMap[item.status],
      badgeVariant: item.isActionRequired ? "destructive" : ExternalProviderBatchStudentMergedStatusBadgeVariants[item.status],
      secondaryBadge: TRAINING_ITEM_TYPE_LABELS[item.itemType],
      secondaryBadgeVariant: "default",
    };
  }

  // Handle LessonTrainingItemVM and ProviderBatchTrainingItemVM
  return {
    avatarInitials: getInitials(item.details.label),
    primaryText: item.title,
    secondaryText: formatDateRange(item.interval.start, item.interval.end),
    tertiaryText: item.details.label,
    badge: item.isActionRequired ? "Action requise" : TRAINING_ITEM_STATUS_LABELS[item.status],
    badgeVariant: item.isActionRequired ? "destructive" : mapStatusToBadgeVariant(item.status),
    secondaryBadge: TRAINING_ITEM_TYPE_LABELS[item.itemType],
    secondaryBadgeVariant: "default",
  };
}

const TEACHER_LESSON_STATUS_LABELS: Record<TeacherLessonListVM["status"], string> = {
  paid: "Payé",
  in_payout: "Paiement en cours",
  to_pay: "À payer",
  cancelled: "Annulé",
};

const TEACHER_LESSON_STATUS_VARIANTS: Record<TeacherLessonListVM["status"], ActivityCardEntry["badgeVariant"]> = {
  paid: "success",
  in_payout: "default",
  to_pay: "warning",
  cancelled: "default",
};

export function formatTeacherLesson(lesson: TeacherLessonListVM): ActivityCardEntry {
  return {
    avatarInitials: getInitials(lesson.student.fullName),
    avatarUrl: lesson.student.profilePictureUrl,
    primaryText: lesson.student.fullName,
    secondaryText: formatLessonDateAndHours(lesson.date, lesson.hours),
    tertiaryText: teacherGetLocationName(lesson.location),
    badge: TEACHER_LESSON_STATUS_LABELS[lesson.status],
    badgeVariant: TEACHER_LESSON_STATUS_VARIANTS[lesson.status],
  };
}

export function formatTrainingToEntry(training: { tag: string | null; title: string; price: number }): ActivityCardEntry {
  return {
    avatarInitials: training.tag?.substring(0, 2).toUpperCase() || "FO",
    primaryText: training.title,
    secondaryText: training.tag || "",
    tertiaryText: formatToCurrency(training.price),
    badge: null,
    badgeVariant: "default",
  };
}

export function formatUnpaidLesson(lesson: UnpaidLessonVM): ActivityCardEntry {
  const fullName = `${lesson.student.name} ${lesson.student.lastName}`;
  return {
    avatarInitials: getInitials(fullName),
    primaryText: fullName,
    secondaryText: formatLessonDateAndHours(lesson.date, lesson.hours),
    tertiaryText: `Enregistré le ${formatDateToLocale(lesson.creationDate)}`,
    badge: "À payer",
    badgeVariant: "warning",
  };
}

const TEACHER_PAYOUT_STATUS_LABELS: Record<TEACHER_PAYOUT_STATUS.PENDING | TEACHER_PAYOUT_STATUS.APPROVED, string> = {
  [TEACHER_PAYOUT_STATUS.PENDING]: "En cours",
  [TEACHER_PAYOUT_STATUS.APPROVED]: "Payé",
};

const TEACHER_PAYOUT_STATUS_VARIANTS: Record<
  TEACHER_PAYOUT_STATUS.PENDING | TEACHER_PAYOUT_STATUS.APPROVED,
  ActivityCardEntry["badgeVariant"]
> = {
  [TEACHER_PAYOUT_STATUS.PENDING]: "warning",
  [TEACHER_PAYOUT_STATUS.APPROVED]: "success",
};

export function formatTeacherPayout(payout: TeacherPayoutVM): ActivityCardEntry {
  const period = `${formatDateToLocale(payout.payout.interval.start)} au ${formatDateToLocale(payout.payout.interval.end)}`;

  return {
    avatarInitials: "FA",
    avatarUrl: null,
    primaryText: period,
    secondaryText: `Facture ${payout.payout.invoice.number}`,
    tertiaryText: `${payout.lessons.totalHours}h - ${payout.lessons.count} cours`,
    badge: TEACHER_PAYOUT_STATUS_LABELS[payout.payout.status],
    badgeVariant: TEACHER_PAYOUT_STATUS_VARIANTS[payout.payout.status],
  };
}

export function formatPayoutLesson(lesson: PayoutLessonVM): ActivityCardEntry {
  const fullName = `${lesson.student.name} ${lesson.student.lastName}`;

  return {
    avatarInitials: getInitials(fullName),
    avatarUrl: null,
    primaryText: fullName,
    secondaryText: formatLessonDateAndHours(lesson.lessonDate, lesson.hours),
    tertiaryText: `Enregistré le ${formatDateToLocale(lesson.creationDate)}`,
    badge: null,
    badgeVariant: "default",
  };
}
